import _ from 'lodash'
import type {Rule, AtRule} from 'postcss'
import postcssSafeParser from 'postcss-safe-parser'
import type {Property, ParsedStyleItem, StyleItemWithMetadata, SelectorsMapping} from './types'

const MOBILE_VARIANT_QUERY = 'value(mobileMediaQuery)'
const PROPS_TO_IGNORE = ['-st-from', '-st-extends', '-st-named']
const styleItemSelectorType = {
    ROOT: 'root',
    STATE: 'state'
}

const getRuleProperties = (rule: Rule, propertiesMapping: Record<string, string> = {}): Property[] => {
    let ruleProperties: Property[] = []

    const isDuplicatedProperty = (propertyName: string): boolean =>
        ruleProperties.some(property => property.prop === propertyName)

    const filterDuplicatedProperties = (propertyName: string): Property[] =>
        ruleProperties.filter(property => property.prop !== propertyName)

    rule.walkDecls(decl => {
        if (PROPS_TO_IGNORE.includes(decl.prop)) {
            return
        }

        const mappedPropName = propertiesMapping[decl.prop]
        const propertyName = mappedPropName || decl.prop

        if (isDuplicatedProperty(propertyName)) {
            ruleProperties = filterDuplicatedProperties(propertyName)
        }

        ruleProperties.push({
            cssProp: decl.prop,
            prop: propertyName,
            value: decl.value
        })
    })

    return ruleProperties
}

const initiateStyleObject = (root?: string): ParsedStyleItem['parsedStyle'][0] => {
    return {
        properties: [],
        propertiesOverride: {},
        mobilePropertiesOverride: {},
        mobileProperties: [],
        metadata: {
            type: root ? styleItemSelectorType.STATE : styleItemSelectorType.ROOT,
            root: root ?? null
        }
    }
}

const inheritDesktopPropertiesInMobileProperties = (parsedStyleItem: ParsedStyleItem): void => {
    const hasMobileStyles = Object.values(parsedStyleItem.parsedStyle).some(style => style.mobileProperties.length > 0)
    Object.values(parsedStyleItem.parsedStyle).forEach(style => {
        if (hasMobileStyles || parsedStyleItem?.metadata?.isMobileOnly) {
            const isMobilePropExists = (property: Property) =>
                style.mobileProperties.some(mobileProperty => mobileProperty.prop === property.prop)
            style.mobileProperties = [
                ...style.properties.filter(property => !isMobilePropExists(property)),
                ...style.mobileProperties
            ]
        }
    })
}

const parseStylableStyleItems = (
    styleItems: StyleItemWithMetadata[],
    options: {selectorsMappings?: SelectorsMapping}
): ParsedStyleItem[] => {
    return styleItems.map(styleItem => {
        const parsedStyleItem: ParsedStyleItem = {...styleItem, parsedStyle: {}}
        const ast = postcssSafeParser(parsedStyleItem.style.properties['$st-css'])

        ast.walkRules(rule => {
            const isMediaQuery = rule?.parent?.type === 'atrule'
            const isMobileVariantRule =
                isMediaQuery && (rule?.parent as unknown as AtRule).params === MOBILE_VARIANT_QUERY
            /**
             * "atrule" refers to mediaQuery. Relevant properties:
             * name - what follows @
             * params - the value, that follows name,
             * for example, for query "media(mobileVariant) {}"
             * name would contain "media" and params "mobileVariant"
             */

            const isUnknownMediaQuery = isMediaQuery && !isMobileVariantRule
            const selectorOptions = options?.selectorsMappings?.[rule.selector]

            if (isUnknownMediaQuery || !selectorOptions) {
                return
            }

            const {name, root, properties, removePrefix = false} = selectorOptions

            const ruleProperties = getRuleProperties(rule as unknown as Rule, properties)

            const isPropertyAlreadyExists = (property: Property): boolean => {
                return ruleProperties.some(ruleProperty => ruleProperty.prop === property.prop)
            }

            const processName = (currentName: string) => {
                if (!parsedStyleItem.parsedStyle[currentName]) {
                    parsedStyleItem.parsedStyle[currentName] = initiateStyleObject(root)
                }

                parsedStyleItem.parsedStyle[currentName].removePrefix = removePrefix

                const getPropertiesWithoutOverrides = (
                    propertiesType: keyof Pick<ParsedStyleItem['parsedStyle'][0], 'properties' | 'mobileProperties'>
                ): Property[] => {
                    return parsedStyleItem.parsedStyle[currentName][propertiesType].filter(
                        property => !isPropertyAlreadyExists(property)
                    )
                }

                if (isMobileVariantRule) {
                    parsedStyleItem.parsedStyle[currentName].mobileProperties = [
                        ...getPropertiesWithoutOverrides('mobileProperties'),
                        ...ruleProperties
                    ]
                } else {
                    parsedStyleItem.parsedStyle[currentName].properties = [
                        ...getPropertiesWithoutOverrides('properties'),
                        ...ruleProperties
                    ]
                }
            }

            if (_.isString(name)) {
                processName(name)
            } else {
                name.forEach(processName)
            }
        })

        inheritDesktopPropertiesInMobileProperties(parsedStyleItem)

        return parsedStyleItem
    })
}

export {parseStylableStyleItems, styleItemSelectorType, MOBILE_VARIANT_QUERY, PROPS_TO_IGNORE}
